import { nicheRollup, districtOf, UNLOCK_SETTING_KEY } from "../data/industryRollup";

// Everything nicheRollup() needs for one account, read with whichever
// Supabase client is passed in ("db"): the app passes its signed-in browser
// client (nicheRollupApi.js); the MCP can pass its own later. Every query is
// filtered by agencyId as well as by the database rules, because the MCP's
// service key ignores the rules. Nothing stops at Supabase's 1,000-row limit:
// tables are read a page at a time, and the database function sends one value
// with its own counts, so a short reply is noticed (complete: false).
// No React and no browser-only code, so the MCP Worker can import it.

// Supabase sends at most 1,000 rows per request.
const PAGE_SIZE = 1000;
// District look-ups go in the address, so ask for a few hundred at a time.
const DISTRICTS_PER_REQUEST = 200;

const JOB_COLUMNS = "id, agency_id, niche_id, status, keywords, locations, devices, samples_per_run, rounds, completed_units, skipped_units, started_at, completed_at, niches(name)";
const SAMPLE_COLUMNS = "id, job_id, run_id, sample_number, completed_units, skipped_units, planned_at, actual_started_at, actual_finished_at";

// The roll-up for one account: nicheRollup()'s result.
export async function loadNicheRollup(db, agencyId) {
  const data = await loadNicheRollupData(db, agencyId);
  return nicheRollup(data, data.now);
}

// The rows nicheRollup() counts from. Throws if anything but the setting
// can't be read; a missing setting just leaves every niche locked.
export async function loadNicheRollupData(db, agencyId) {
  if (!agencyId) throw new Error("loadNicheRollupData: an account (agencyId) is required");
  const [jobs, samples, keywordRows, listingLinks, setting, rows] = await Promise.all([
    loadAll(db, "jobs", JOB_COLUMNS, agencyId),
    loadAll(db, "samples", SAMPLE_COLUMNS, agencyId),
    loadAll(db, "keywords", "id, job_id, raw_text, standard_form", agencyId),
    loadAll(db, "advertiser_links", "id, listing_key, domain, action, status", agencyId, { status: "applied" }),
    loadSetting(db),
    loadRollupRows(db, agencyId),
  ]);

  const nicheJobs = jobs.filter(j => j.niche_id);
  const postcodes = [...nicheJobs.flatMap(j => j.locations || []), ...rows.searchKeys.map(k => k.location)];
  const districts = await loadDistricts(db, [...new Set(postcodes.map(districtOf).filter(Boolean))]);

  return { jobs, samples, keywordRows, listingLinks, setting, districts, ...rows, now: new Date().toISOString() };
}

// Every row of the account's table, a page at a time, in id order.
async function loadAll(db, table, columns, agencyId, where = {}) {
  const all = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    let query = db.from(table).select(columns).eq("agency_id", agencyId);
    for (const [column, value] of Object.entries(where)) query = query.eq(column, value);
    const { data, error } = await query.order("id", { ascending: true }).range(from, from + PAGE_SIZE - 1);
    if (error) throw new Error(`${table}: ${error.message}`);
    if (!data || data.length === 0) break;
    all.push(...data);
    if (data.length < PAGE_SIZE) break;
  }
  return all;
}

// The unlock targets' value, or null if it can't be read.
async function loadSetting(db) {
  const { data, error } = await db.from("app_settings").select("value").eq("key", UNLOCK_SETTING_KEY).maybeSingle();
  if (error) console.error("loadSetting", error);
  return data ? data.value : null;
}

// The database function's one reply (audit_rollup_rows), with complete:
// false if any list holds fewer rows than the function counted.
async function loadRollupRows(db, agencyId) {
  const { data, error } = await db.rpc("audit_rollup_rows", { p_agency_id: agencyId });
  if (error) throw new Error(`audit_rollup_rows: ${error.message}`);
  const reply = data || {};
  const lists = {
    searchRecords: reply.search_records || [],
    searchKeys: reply.search_keys || [],
    advertiserRows: reply.advertiser_rows || [],
  };
  const counts = reply.counts || {};
  const complete = lists.searchRecords.length === counts.search_records
    && lists.searchKeys.length === counts.search_keys
    && lists.advertiserRows.length === counts.advertiser_rows;
  return { ...lists, complete };
}

// Map positions for these districts: [{ outcode, lat, lng }].
async function loadDistricts(db, outcodes) {
  const all = [];
  for (let i = 0; i < outcodes.length; i += DISTRICTS_PER_REQUEST) {
    const { data, error } = await db
      .from("postcode_districts")
      .select("outcode, lat, lng")
      .in("outcode", outcodes.slice(i, i + DISTRICTS_PER_REQUEST))
      .order("outcode", { ascending: true });
    if (error) throw new Error(`postcode_districts: ${error.message}`);
    all.push(...(data || []));
  }
  return all;
}
